import { DEFAULT_MAX_PRECISION } from "../../../global";
import {
  formatNumberToSpecialValues,
  formatSpecialValuesToNumber,
} from "../../utils";
import convert from "../convert-units/lib/convert_units";

const customUnitMap = {
  time: {
    baseUnit: "s",
    customUnits: [
      "h:min:s:ms",
      "h:min:s",
      "h:min",
      "min:s:ms",
      "min:s",
      "s:ms",
      "min:s:ds",
      "s:ds:ms:μs",
      "s:ds",
      "h:min:s:cs",
      "min:s:cs",
      "s:cs",
      "s:ms:μs",
      "min:s:ms:μs",
      "h:min:s:ms:μs",
      "ms:μs",
    ],
  },
  angle: {
    baseUnit: "°",
    customUnits: [`° ' "`, `' "`, `° '`],
  },
};

const relativeUnits = ["%", "ppm"];

const isCustomUnit = (unit) => {
  for (const [key, value] of Object.entries(customUnitMap)) {
    if (value.customUnits.includes(unit)) {
      return true;
    }
  }
  return false;
};

export const isCovertPossible = (from, to) => {

  // Handle % to % conversion manually
  if (from?.trim() === "%" && to?.trim() === "%") {
    return true;
  }

  if (from === "h:min:s:ms") {
    from = "ms";
  }

  let possibilities;
  try {
    possibilities = convert().from(from).possibilities();
    return possibilities?.includes(to) || false;
  } catch (e) {
    console.log(
      "[ERROR] Failed to check unit conversion possibility, error: ",
      e
    );
    return false;
  }
};

const getBaseUnitForCustomUnit = (unit) => {
  for (const [key, value] of Object.entries(customUnitMap)) {
    if (value.customUnits.includes(unit)) {
      return value.baseUnit;
    }
  }
  return null;
};

export const getBaseUnit = (unit) => {
  try {
    // return null for empty or null units
    if (!unit || unit.trim() === "") {
      return null;
    }

    // for relative units return null
    if (unit.includes("%") || unit.includes("ppm")) {
      return null;
    }

    // Check if the unit is a custom unit
    let baseUnit = getBaseUnitForCustomUnit(unit);
    if (baseUnit) {
      return baseUnit;
    }

    // Get the base unit for standard units
    const { measure, system } = convert().describe(unit);
    const measures = convert()._measures;
    const anchor = measures[measure]._anchors["metric"];
    return anchor.unit;
  } catch (e) {
    // console.error(`[ERROR] Failed to get base unit for ${unit}, error: `, e);
    return null;
  }
};

export const relativeUnitConvertor = (val, from, to, baseValue = null) => {

  // same relative unit -> no conversion
  if (from?.trim() && from?.trim() === to?.trim()) {
    return val;
  }

  if (!baseValue || (from?.includes("%") && to?.includes("%"))) {
    return val;
  }
  baseValue = String(baseValue || "").trim();
  let baseValueUnit = baseValue?.split("#")[1];
  baseValue = Number(baseValue?.split("#")[0]);

  let res = val;

  if (from?.trim() === "ppm") {
    res = (val / 1000000) * (baseValue || 1);
  } else if (to?.trim() === "ppm") {
    res = val * 1000000 * (baseValue || 1);
  } else if (from?.trim() === "%" && baseValue != null) {
    res = (Math.abs(Number(val)) * Math.abs(Number(baseValue))) / 100;
    if (baseValueUnit) {
      res = unitConvertor(res, baseValueUnit, to, null);
    }
  } else if (to?.trim() === "%" && baseValue != null) {
    res = unitConvertor(Math.abs(Number(val)), from, baseValueUnit, null);

  // 🔹 If val is exactly 0 → return "Infinity"
      if (Number(val) === 0) {
        res = "Infinity";
      } else {
        res = (res * 100) /  Math.abs(Number(baseValue));
      }

    if (baseValueUnit) {
    }
  } else {
    res = unitConvertor(val, from, to);
  }

  return res;
};

export default function unitConvertor(value, fromUnit, toUnit, baseValue,type=null) {
  if (String(value).includes("$")) {
    value = value.replaceAll("$", "");
  }
  let convertedValue = value;

  // If both units are %, don't convert
  if (fromUnit === "%" && toUnit === "%") {
    return value;
  }

    // Same unit -> same unit: nothing to convert (%, ppm, mm, s, etc.)
  if (fromUnit?.trim() && fromUnit?.trim() === toUnit?.trim()) {
    return value;
  }

  // Unary unit coversions
  // convert relative units to absolute units
  if (relativeUnits.includes(fromUnit) || relativeUnits.includes(toUnit)) {
    convertedValue = relativeUnitConvertor(value, fromUnit, toUnit, baseValue);
    return convertedValue;
  }

  // Binary unit coversions
  if (!fromUnit || !toUnit) {
    return convertedValue;
  }

  // custom to custom unit conversion
  if (isCustomUnit(fromUnit) && isCustomUnit(toUnit)) {
    // TODO: implement custom to custom unit conversion
  }

  // ignore cross measure unit conversion
  if (getBaseUnit(fromUnit) !== getBaseUnit(toUnit)) {
    return convertedValue;
  }

  // standard to custom unit conversion
  if (!isCustomUnit(fromUnit) && isCustomUnit(toUnit)) {
    if (fromUnit == "s") {
      value = convert(value).from(fromUnit.trim()).to("μs");
      fromUnit = "μs";
      convertedValue = formatNumberToSpecialValues(value, toUnit);
    } else if (fromUnit == "°") {
      value = convert(value).from(fromUnit.trim()).to("mas");
      fromUnit = "mas";
      convertedValue = formatNumberToSpecialValues(value, toUnit);
    }
    return convertedValue;
  }

  // process special values to numerical values, custom units to standard units
  if (isCustomUnit(fromUnit) && !isCustomUnit(toUnit)) {
    let specialValueUnit, currentSpecialValueUnit;
    [value, specialValueUnit, currentSpecialValueUnit] =
      formatSpecialValuesToNumber(value, fromUnit);
    if (currentSpecialValueUnit !== "number") {
      fromUnit = currentSpecialValueUnit;
    }
  }

  // standard unit conversion
  if (fromUnit === toUnit) return value;
  try {
    convertedValue = convert(value).from(fromUnit.trim()).to(toUnit.trim());
  } catch (e) {
    console.log(
      `[ERROR] Failed to convert unit (from: ${fromUnit}, to: ${toUnit}), error: `,
      e
    );
    return convertedValue;
  }

  // return convertedValue;
    return Number(convertedValue)?.toFixed?.(DEFAULT_MAX_PRECISION)?.replace?.(/\.?0+$/, "")?.trim?.();
}


export const convertMasterPercentValue = (columnValue, calibrationPoint, rangeStr, isFSD = false) => {
  const firstRange = rangeStr?.split("||")?.[0] || "";
  const [fromRaw, toRaw] = firstRange?.split("|") || [];

  const fromVal = Math.abs(parseFloat(fromRaw?.split("#")?.[0]));
  const toVal = Math.abs(parseFloat(toRaw?.split("#")?.[0]));

  const rangeValue = fromVal + toVal;

  return isFSD
    ? (columnValue / 100) * rangeValue
    : (columnValue / 100) * calibrationPoint;
};

export const convertDUCPercentValue = (columnValue, calibrationPoint, rangeStr, isFSD = false) => {
  const firstRange = rangeStr?.split("||")?.[0] || "";
  const [fromRaw, toRaw] = firstRange?.split("|") || [];

  const rangeLow = parseFloat(fromRaw?.split("#")?.[0]);
  const rangeHigh = parseFloat(toRaw?.split("#")?.[0]);

  return isFSD
    ? ((rangeHigh - rangeLow) * columnValue) / 100
    : (columnValue * calibrationPoint) / 100;
};

export const convertClassValue = (unit, calibrationPoint) => {
  const cp = parseFloat(calibrationPoint) || 0;

  // Extract last character (A / B / C)
  const lastChar = String(unit).trim().slice(-1).toUpperCase();

  switch (lastChar) {
    case "A":
      return 0.15 + 0.002 * cp;

    case "B":
      return 0.3 + 0.005 * cp;

    case "C":
      return 0.6 + 0.01 * cp;

    default:
      return null; // invalid class
  }
};




